import { z } from 'zod';
import { MoneySchema } from './common.js';
// Wire shape for Decimal columns — Prisma emits canonical strings; accept
// either string or non-negative number (matches AR / loan / prepaid).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// ============================================================
// AccrualEntryStatus enum (mirrors Prisma)
// ============================================================
export const AccrualEntryStatusSchema = z.enum(['PENDING', 'POSTED', 'REVERSED']);
// ============================================================
// AccrualTemplate — recurring rule
// ============================================================
export const AccrualTemplateSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    code: z.string().min(1),
    name: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    expenseAccountCode: z.string().min(1),
    accruedLiabilityAccountCode: z.string().min(1),
    monthlyAmount: DecimalLike,
    isActive: z.boolean(),
    startDate: z.coerce.date(),
    endDate: z.coerce.date().nullable().optional(),
    notes: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row — adds derived display name(s).
export const AccrualTemplateListRowSchema = AccrualTemplateSchema.extend({
    branchName: z.string().nullable().optional(),
});
// ============================================================
// CreateAccrualTemplate — POST body
//
// `expenseAccountCode` should be a 5xxx PAE code; we validate the prefix
// loosely (5*) without locking to a closed enum so future categories don't
// require a schema bump. `accruedLiabilityAccountCode` is optional — when
// omitted the service resolves the ACCRUED_EXPENSE key per-company.
// ============================================================
export const CreateAccrualTemplateSchema = z.object({
    code: z.string().min(1),
    name: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    expenseAccountCode: z
        .string()
        .min(1)
        .regex(/^[56]/, 'expenseAccountCode must be a 5xxx (cost) or 6xxx (expense) PAE code'),
    accruedLiabilityAccountCode: z.string().min(1).optional(),
    monthlyAmount: z.coerce.number().positive(),
    isActive: z.boolean().default(true),
    startDate: z.iso.date(),
    endDate: z.iso.date().nullable().optional(),
    notes: z.string().nullable().optional(),
});
// ============================================================
// UpdateAccrualTemplate — PATCH body (all fields optional)
// ============================================================
export const UpdateAccrualTemplateSchema = z.object({
    name: z.string().min(1).optional(),
    branchId: z.uuid().nullable().optional(),
    expenseAccountCode: z.string().min(1).optional(),
    accruedLiabilityAccountCode: z.string().min(1).optional(),
    monthlyAmount: z.coerce.number().positive().optional(),
    isActive: z.boolean().optional(),
    startDate: z.iso.date().optional(),
    endDate: z.iso.date().nullable().optional(),
    notes: z.string().nullable().optional(),
});
// ============================================================
// AccrualEntry — materialised row
// ============================================================
export const AccrualEntrySchema = z.object({
    id: z.uuid(),
    templateId: z.uuid(),
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
    amount: DecimalLike,
    status: AccrualEntryStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    reversalJournalEntryId: z.uuid().nullable().optional(),
    postedAt: z.coerce.date().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    reversedAt: z.coerce.date().nullable().optional(),
    reversedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row — joined template context for the outstanding/by-period view.
export const AccrualEntryListRowSchema = AccrualEntrySchema.extend({
    templateCode: z.string(),
    templateName: z.string(),
    branchId: z.uuid().nullable().optional(),
    branchName: z.string().nullable().optional(),
    expenseAccountCode: z.string(),
});
// ============================================================
// POST /accruals/run — batch create + post for a period
// ============================================================
export const RunAccrualsSchema = z.object({
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
});
export const RunAccrualsResultSchema = z.object({
    period: z.string(),
    templatesEligible: z.number().int().nonnegative(),
    entriesCreated: z.number().int().nonnegative(),
    entriesSkipped: z.number().int().nonnegative(),
    journalEntryId: z.uuid().nullable(),
    journalVoucherNo: z.string().nullable(),
    totalAmount: DecimalLike,
});
// ============================================================
// POST /accruals/reverse — reverse a posted period's accruals
//
// Typically invoked for period N at the start of period N+1, but the
// endpoint takes an explicit period so we can reverse historical batches
// independently (e.g. backfills).
// ============================================================
export const ReverseAccrualsSchema = z.object({
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
});
export const ReverseAccrualsResultSchema = z.object({
    period: z.string(),
    entriesReversed: z.number().int().nonnegative(),
    entriesSkipped: z.number().int().nonnegative(),
    journalEntryId: z.uuid().nullable(),
    journalVoucherNo: z.string().nullable(),
    totalAmount: DecimalLike,
});
//# sourceMappingURL=accrual.js.map